import { ConferenceListTable } from './ui/ConferenceTable';
import { GettingStartedPanel } from './ui/GettingStartedPanel';
import Error from '../Error';
import { useState, useEffect } from 'react';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import {
  Alert,
  Box,
  Container,
  Grid,
  Header,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-console';
import { TaskDetail } from '../../ConferencePages/conferenceInfo/ui/viewConference';
import {
  LaunchTask,
  ListLaunchTasksCommand,
  ListLaunchTasksCommandOutput,
  ListVenuesCommand,
  ListVenuesCommandOutput,
  VenueInfoItem,
  GetLaunchTaskCommand,
  GetLaunchTaskCommandOutput,
  GetEntityBuildRequestInfoCommand,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';

import { useAuthState } from '../../authentication/context';
import {
  useNavDispatch,
  overrideDetailBreadcrumb,
} from '../../components/navigation/context';
import { EditBuddyLaunchPage } from '../conferenceInfo/ui/editBuddyLaunches';

import { Packages } from './ui/PackagesEditor/Packages';
import EditWriterSection from './ui/editWriterSection';
import AdvanceErrorHandlingPage from '../AdvancedErrorPage';

import { ModificationView } from '../conferenceInfo/ui/editPermissions';
import { PermissionsView } from './ui/PermissionsView';
import { callApi } from '../../client';
import { venueDisplayName as venueDisplayNameFor } from '../../utils/venueDisplay';
import { NoTokenError } from '../../errors/NoTokenError';
import { ApiErrorProvider } from '../../components/ApiErrorContext';
import { ApiErrorNotifications } from '../../components/ApiErrorNotifications';
import { paginateAll, PAGE_SIZE } from '../../utils/pagination';
import {
  useConferenceVenueScope,
  ALL_CONFERENCES,
  ALL_VENUES,
} from '../../hooks/useConferenceVenueScope';
import { useLatestRequest } from '../../hooks/useLatestRequest';

export function ConferenceList() {
  const [LaunchData, setLaunchData] = useState<LaunchTask[]>([]);
  const [LaunchDataLoading, setLaunchDataLoading] = useState(false);
  const [FetchLaunchTaskListSuccess, setFetchLaunchTaskListSuccess] =
    useState(true);

  const { path } = useRouteMatch();
  const { isAuthenticated, token } = useAuthState();
  const navDispatch = useNavDispatch();

  const sampleTaskListObj: LaunchTask[] = [];

  const location = useLocation();

  const [launchTaskDetail, setLaunchTaskDetail] = useState(
    sampleTaskListObj[0]
  );

  const [detailViewLoadingStatus, setDetailViewLoadingStatus] = useState(true);
  const [detailViewFetchStatusCode, setDetailViewFetchStatusCode] =
    useState(-1);
  const [detailViewFetchSuccess, setDetailViewFetchSuccess] = useState(true);

  const [buddyLaunches, setBuddyLaunches] = useState<LaunchTask[]>([]);
  const [buddyLaunchPageLoadingStatus, setBuddyLaunchesPageLoadingStatus] =
    useState(true);
  const [buddyLaunchFetchSuccess, setBuddyLaunchFetchSuccess] = useState(true);

  const [errorMsg, setErrorMsg] = useState('');

  // Server-side scope filter (mirrors ActivePackages): conference defaults to "All accessible" (D6),
  // venue requires a conference to be chosen first.
  const [venues, setVenues] = useState<VenueInfoItem[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  // A failed venue load is non-fatal (the list still works via the All accessible default), but the
  // scope dropdowns go silently empty — flag it inline so the coordinator knows why (CR-301380769).
  const [venuesError, setVenuesError] = useState(false);

  // Shared conference/venue scope state + derived Select options (D6).
  const scope = useConferenceVenueScope(venues);
  // Latest-request-wins guard: narrowing scope while a broad "All accessible" walk is in flight must
  // not let the slow broad result overwrite the newer narrow one (finding on CR-301380769).
  const launchReq = useLatestRequest();

  async function fetchVenues() {
    if (!token) return;
    setVenuesLoading(true);
    try {
      const response: ListVenuesCommandOutput = await callApi(
        new ListVenuesCommand({}),
        token
      );
      if (response.$metadata.httpStatusCode === 200) {
        setVenues(response.venues ?? []);
        setVenuesError(false);
      } else {
        setVenuesError(true);
      }
    } catch (err: any) {
      // Non-fatal: without venues the scope selects stay empty (defaulting to All accessible). Flag it
      // inline rather than only console.error'ing so the empty dropdowns are explained (CR-301380769).
      console.error('Failed to load venues:', err);
      setVenuesError(true);
    } finally {
      setVenuesLoading(false);
    }
  }

  async function callListLaunchesApi(conf?: string, ven?: string) {
    if (!token) throw new NoTokenError();

    // Clear the previous error before retrying so the table's loading spinner shows during an
    // in-flight retry instead of the stale full-page error; a fresh failure flips it back to false
    // (finding on CR-301380769).
    setFetchLaunchTaskListSuccess(true);
    try {
      await launchReq.run(
        setLaunchDataLoading,
        () =>
          // Page through every result rather than the first ≤1000 (tcx-core-platforms-903 §8 F7).
          paginateAll<LaunchTask>((nextToken) =>
            callApi(
              new ListLaunchTasksCommand({
                conference: conf,
                venue: ven,
                pageSize: PAGE_SIZE,
                nextToken,
              }),
              token
            ).then((response: ListLaunchTasksCommandOutput) => ({
              items: response.tasks ?? [],
              nextToken: response.nextToken,
              statusCode: response.$metadata.httpStatusCode ?? 0,
            }))
          ),
        ({ items, ok, truncated }) => {
          // Commit only on a fully-successful, non-truncated walk. A truncated result means more pages
          // remain than the cap allows — surface that as an error rather than silently showing a
          // partial list (CR-301380769 rev2). The superseded-fetch drop and the loading-flag reset are
          // owned by run() (CR-301380769 rev5).
          if (ok && !truncated) {
            setFetchLaunchTaskListSuccess(true);
            setLaunchData(items);
          } else {
            setFetchLaunchTaskListSuccess(false);
            setErrorMsg(
              ok
                ? 'Too many pages of launch tasks to load; please narrow the conference/venue filter.'
                : 'Failed to fetch launch tasks'
            );
          }
        }
      );
    } catch (err: any) {
      setFetchLaunchTaskListSuccess(false);
      setErrorMsg(err.message);
    }
  }

  async function callGetLaunchApi(id: string) {
    if (!token) throw new NoTokenError();
    try {
      const response: GetLaunchTaskCommandOutput = await callApi(
        new GetLaunchTaskCommand({
          launchId: id,
        }),
        token
      );
      setDetailViewLoadingStatus(false);
      if (response.$metadata.httpStatusCode == 200) {
        setDetailViewFetchStatusCode(response.$metadata.httpStatusCode);
        if (response.task) {
          setLaunchTaskDetail(response.task);
          setDetailViewFetchSuccess(true);
          // Optional breadcrumb label enhancement: show the task's name for the
          // detail crumb once it's known (falls back to the raw view-<id>
          // segment otherwise). Mirrors the group-detail behavior.
          overrideDetailBreadcrumb(
            navDispatch,
            `/conferences/view-${id}`,
            response.task.name
          );
          const children = response.task?.childTask;
          if (children) {
            const currentLocation = location.pathname.split('/');
            if (
              currentLocation.length === 3 ||
              (currentLocation.length === 4 &&
                currentLocation[3] === 'buddylaunches')
            )
              void callGetBuddyLaunchApi(children);
          } else {
            setBuddyLaunches([]);
            setBuddyLaunchesPageLoadingStatus(false);
            setBuddyLaunchFetchSuccess(true);
          }
        }
      }
    } catch (err: any) {
      setDetailViewLoadingStatus(false);
      setDetailViewFetchStatusCode(err.statusCode);
      setErrorMsg(err.message);
      setDetailViewFetchSuccess(false);
    }
  }

  async function callGetBuddyLaunchApi(idList: string[]) {
    setBuddyLaunchesPageLoadingStatus(true);
    if (!token) throw new NoTokenError();
    const apiPromises = idList.map((id) => {
      return callApi(
        new GetLaunchTaskCommand({
          launchId: id,
        }),
        token
      );
    });
    const apiResponses = await Promise.all(apiPromises);

    const allSuccessful = apiResponses.every(
      (response: GetLaunchTaskCommandOutput) =>
        response && response.$metadata.httpStatusCode === 200
    );
    setBuddyLaunchFetchSuccess(allSuccessful);
    setBuddyLaunchesPageLoadingStatus(false);
    if (allSuccessful) {
      const newFetchResults: LaunchTask[] = apiResponses
        .map((response: GetLaunchTaskCommandOutput) => response.task)
        .filter(Boolean);
      setBuddyLaunches((prevList) => [...prevList, ...newFetchResults]);
    }
  }

  useEffect(() => {
    console.log('pAtHnAmE', location.pathname);
    const currentLocation = location.pathname.split('/');
    if (isAuthenticated) {
      // On the list route, load the venue list once to seed the Conference/Venue scope selects.
      if (currentLocation.length === 2) {
        void fetchVenues();
      }

      if (currentLocation.length === 3 || currentLocation.length === 4) {
        setDetailViewLoadingStatus(true);
        console.log('id', currentLocation[2].substring(5));
        void callGetLaunchApi(currentLocation[2].substring(5));
      }
    }
    // token is in the dep array because both branches use it: fetchVenues() and callGetLaunchApi()
    // early-return when !token. If isAuthenticated flips true before token is populated, omitting
    // token would fetch once, bail, and never retry — leaving the Conference/Venue scope dropdowns
    // (and the detail view) permanently empty. Keyed on token so it re-runs once the token lands,
    // matching the sibling scoped-list effect below (finding on CR-301380769 rev3).
  }, [isAuthenticated, token, location.pathname]);

  // Fetch (and re-fetch) the scoped launch list whenever the selected scope changes, while on the
  // list route. An ALL_CONFERENCES scope sends no conference so the backend returns every conference
  // the caller can read (D6).
  useEffect(() => {
    const currentLocation = location.pathname.split('/');
    if (isAuthenticated && token && currentLocation.length === 2) {
      setLaunchDataLoading(true);
      void callListLaunchesApi(scope.scopedConference, scope.scopedVenue);
    }
  }, [
    isAuthenticated,
    token,
    location.pathname,
    scope.conference,
    scope.venue,
  ]);

  // Display-only venue name for the table column: coordinator canonical override from the venue list,
  // else the raw venue (sorting/filtering/identity stay on the raw venue). tcx-core-platforms-903 D2.
  const venueDisplayName = (conf?: string, ven?: string): string =>
    venueDisplayNameFor(venues, conf, ven);

  // Manual refresh of the launch task list for the current scope (tcx-core-platforms-1133), mirroring
  // the Active packages table's Refresh button. Reuses the scoped fetch so the latest-request guard
  // and truncation/error handling in callListLaunchesApi apply.
  const refreshLaunches = () => {
    void callListLaunchesApi(scope.scopedConference, scope.scopedVenue);
  };

  const scopeFilterBar = (
    <Container
      header={
        <Header
          variant='h2'
          description='Scope the launch list to a conference (and optionally one venue within it). Defaults to every conference you can access.'
        >
          Filter
        </Header>
      }
    >
      <SpaceBetween size='s'>
        {venuesError && (
          <Alert
            type='warning'
            dismissible
            onDismiss={() => setVenuesError(false)}
          >
            Could not load conference/venue filters — showing all accessible
            results.
          </Alert>
        )}
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <Box>
            <Box variant='awsui-key-label'>Conference</Box>
            <Select
              selectedOption={scope.selectedConferenceOption}
              onChange={({ detail }) =>
                scope.selectConference(detail.selectedOption.value)
              }
              options={scope.conferenceOptions}
              placeholder='All accessible'
              loadingText='Loading conferences...'
              statusType={venuesLoading ? 'loading' : 'finished'}
              empty='No conferences available'
              data-test-id='conference-scope-select'
            />
          </Box>
          <Box>
            <Box variant='awsui-key-label'>Venue</Box>
            <Select
              selectedOption={scope.selectedVenueOption}
              onChange={({ detail }) =>
                scope.setVenue(detail.selectedOption.value ?? ALL_VENUES)
              }
              options={scope.venueOptions}
              disabled={scope.conference === ALL_CONFERENCES}
              placeholder='All venues'
              data-test-id='venue-scope-select'
            />
          </Box>
        </Grid>
      </SpaceBetween>
    </Container>
  );

  return (
    <ApiErrorProvider>
      <div>
        <ApiErrorNotifications />
        <Switch>
          <Route
            exact
            path={path}
            render={() => (
              // Keep the scope filter bar mounted even on a list-fetch failure, so a transient error
              // doesn't strand the user on a full-page error screen with no way to retry. The fetch is
              // keyed on [conference, venue], so changing the scope re-runs it. Only the table area is
              // replaced by the error (tcx-core-platforms-903, finding on CR-301380769).
              <SpaceBetween size='l'>
                {scopeFilterBar}
                <GettingStartedPanel />
                {FetchLaunchTaskListSuccess ? (
                  <ConferenceListTable
                    data={LaunchData}
                    loading={LaunchDataLoading}
                    venueDisplayName={venueDisplayName}
                    onRefresh={refreshLaunches}
                  />
                ) : (
                  <AdvanceErrorHandlingPage errorMsg={errorMsg} />
                )}
              </SpaceBetween>
            )}
          />
          <Route
            path={`${path}/view-:id/assignee`}
            render={() =>
              detailViewFetchSuccess ? (
                <EditWriterSection task={launchTaskDetail} />
              ) : (
                <AdvanceErrorHandlingPage
                  status={detailViewFetchStatusCode}
                  errorMsg={errorMsg}
                />
              )
            }
          />
          <Route
            path={`${path}/view-:id/buddylaunches`}
            render={() => {
              return (
                <EditBuddyLaunchPage
                  launchTaskDetail={launchTaskDetail}
                  buddyLaunches={buddyLaunches}
                  buddyLaunchesLoading={buddyLaunchPageLoadingStatus}
                  buddyLaunchFetchSuccess={buddyLaunchFetchSuccess}
                />
              );
            }}
          />

          <Route
            path={`${path}/view-:id/packages`}
            render={() => {
              if (launchTaskDetail) {
                return (
                  <Packages
                    task={launchTaskDetail}
                    editable={true}
                    onPollStatus={async () => {
                      if (!token) throw new NoTokenError();
                      return callApi(
                        new GetEntityBuildRequestInfoCommand({
                          entityId: launchTaskDetail.launchId,
                          entityType: 'TASK',
                        }),
                        token
                      );
                    }}
                  />
                );
              } else {
                return <Error />;
              }
            }}
          />

          <Route
            path={`${path}/view-:id/permissions`}
            render={({ match }) =>
              detailViewFetchSuccess ? (
                <>
                  <SpaceBetween size='m' direction='vertical'>
                    <ModificationView launchTaskDetail={launchTaskDetail} />
                    <PermissionsView
                      id={match.params.id}
                      workStatus={launchTaskDetail?.workStatus}
                    />
                  </SpaceBetween>
                </>
              ) : (
                <AdvanceErrorHandlingPage
                  status={detailViewFetchStatusCode}
                  errorMsg={errorMsg}
                />
              )
            }
          />

          <Route
            path={`${path}/view-:id`}
            render={({ match }) => {
              const id = match.params.id;
              return (
                <TaskDetail
                  id={id}
                  launchTask={launchTaskDetail}
                  detailLoading={detailViewLoadingStatus}
                  detailFetchStatus={detailViewFetchStatusCode}
                  errorMsg={errorMsg}
                  buddyLaunchesLoading={buddyLaunchPageLoadingStatus}
                  buddyLaunches={buddyLaunches}
                  buddyLaunchFetchSuccess={buddyLaunchFetchSuccess}
                  auditLog={launchTaskDetail?.auditLog}
                />
              );
            }}
          />

          <Route render={() => <Error />} />
        </Switch>
      </div>
    </ApiErrorProvider>
  );
}

export default ConferenceList;
